import { Button, type ControlledSearch, DataTable, Meter, TOUCH } from "@nymbl/nos2-rules";
import { ChevronRight } from "lucide-react";
import type * as React from "react";
import { useMemo } from "react";
import { PersonTags } from "@/components/team/PersonTags";
import type { OpenPerson } from "@/components/team/panel-state";
import { withCells } from "@/lib/table-columns";
import { byName, SUMMARY_TABLE_COLUMNS } from "@/lib/team/details";
import { formatHours, formatPct, type PersonMetrics, sortByAttention } from "@/lib/team/metrics";

interface SummaryTableProps {
  people: PersonMetrics[];
  onOpen: OpenPerson;
  controls: React.ReactNode;
  /** One search across Summary and Details. */
  search: ControlledSearch;
}

/** Unsorted, the rows keep attention order (tagged people first). */
export function SummaryTable({ people, onOpen, controls, search }: SummaryTableProps) {
  const rows = useMemo(() => sortByAttention([...people].sort(byName)), [people]);
  const columns = useMemo(
    () =>
      withCells(SUMMARY_TABLE_COLUMNS, {
        name: {
          className: "min-w-48",
          render: (p) => (
            <>
              <Button
                variant="link"
                className={`h-auto px-0 font-semibold has-[>svg]:px-0 ${TOUCH}`}
                onClick={() => onOpen(p.personId, "summary")}
              >
                {p.personName}
                <ChevronRight aria-hidden="true" />
              </Button>
              <PersonTags person={p} />
            </>
          ),
        },
        available: { render: (p) => formatHours(p.available) },
        plannedPct: {
          className: "min-w-40",
          render: (p) => (
            <>
              <div className="text-sm tabular-nums">
                {p.available === null
                  ? "—"
                  : `${formatHours(p.planned)} of ${formatHours(p.available)} hrs`}
              </div>
              <Meter
                ratio={p.plannedPct ?? 0}
                overTone="warning"
                hideLabel
                label={`Guidance ${formatPct(p.plannedPct)} of available`}
              />
            </>
          ),
        },
        utilization: { render: (p) => formatPct(p.utilization) },
        billablePct: {
          className: "min-w-48",
          render: (p) => (
            <>
              <div className="flex justify-between gap-2 text-sm">
                <span className={p.belowTarget ? "font-semibold text-danger" : "font-semibold"}>
                  {formatPct(p.billablePct)}
                </span>
                <span className="text-muted-foreground">
                  {p.targetPct ? `Target ${formatPct(p.targetPct)}` : "No target"}
                </span>
              </div>
              <Meter
                ratio={Math.min(p.billablePct ?? 0, 1)}
                marker={p.targetPct ?? undefined}
                hideLabel
                label={`Billable ${formatPct(p.billablePct)}, target ${formatPct(p.targetPct)}`}
              />
            </>
          ),
        },
      }),
    [onOpen],
  );

  return (
    <div className="space-y-3">
      <DataTable
        searchState={search}
        label="Team summary"
        title="Team · to date"
        search={{ placeholder: "Search people…" }}
        exportName="my-team-summary"
        controls={controls}
        columns={columns}
        rows={rows}
        rowKey={(p) => p.personId}
      />
      <ul className="flex gap-3 text-sm" aria-label="Legend">
        <li className="flex items-center gap-1.5">
          <span className="h-2 w-4 rounded-full bg-primary" aria-hidden="true" />
          Billable
        </li>
        <li className="flex items-center gap-1.5">
          <span className="h-3 w-0.5 bg-foreground" aria-hidden="true" />
          Target
        </li>
      </ul>
    </div>
  );
}
